import { apiFetch } from "@/lib/api";

export type HotelRoom = {
  id?: number;
  room_name?: string | null;
  room_description?: string | null;
  price_room_only?: number | null;
  price_breakfast?: number | null;
  price_half_board?: number | null;
  price_full_board?: number | null;
  max_occupancy?: number | null;
  room_image_url?: string | null;
};

export type Hotel = {
  id: number;
  hotel_name: string;
  location?: string | null;
  address?: string | null;
  stars?: number | null;
  description?: string | null;
  amenities?: string | null;
  image_url?: string | null;
  cancellation_policy?: string | null;
  rooms?: HotelRoom[];
  created_at?: string;
  updated_at?: string;
};

export type HotelRoomListItem = {
  id: number;
  room_name: string;
};

export type HotelListItem = {
  id: number;
  hotel_name: string;
  location?: string | null;
  stars?: number | null;
  image_url?: string | null;
  rooms?: HotelRoomListItem[];
};

export type HotelListResponse = {
  items: HotelListItem[];
  total: number;
};

export const fetchHotels = async (params: {
  page: number;
  size: number;
  search?: string;
  sortField?: string | null;
  sortOrder?: string | null;
  stars?: number | null;
}): Promise<HotelListResponse> => {
  const query = new URLSearchParams();
  query.set("page", String(params.page));
  query.set("size", String(params.size));
  if (params.search) query.set("search", params.search);
  if (params.sortField) query.set("sortField", params.sortField);
  if (params.sortOrder) query.set("sortOrder", params.sortOrder);
  if (params.stars != null) query.set("stars", String(params.stars));
  const payload = await apiFetch<HotelListResponse | null>(`/hotels?${query.toString()}`);
  return payload ?? { items: [], total: 0 };
};

export const getHotel = async (id: number): Promise<Hotel> => {
  return apiFetch<Hotel>(`/hotels/${id}`);
};

export const createHotel = async (data: Partial<Hotel>): Promise<Hotel> => {
  return apiFetch<Hotel>("/hotels", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const updateHotel = async (id: number, data: Partial<Hotel>): Promise<Hotel> => {
  return apiFetch<Hotel>(`/hotels/${id}` , {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const deleteHotel = async (id: number): Promise<void> => {
  await apiFetch<void>(`/hotels/${id}`, { method: "DELETE" });
};

export const deleteAllHotels = async (): Promise<void> => {
  await apiFetch<void>("/hotels", { method: "DELETE" });
};

export const addHotelRoom = async (hotelId: number, data: Partial<HotelRoom>): Promise<HotelRoom> => {
  return apiFetch<HotelRoom>(`/hotels/${hotelId}/rooms`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const getHotelRoom = async (hotelId: number, roomId: number): Promise<HotelRoom> => {
  return apiFetch<HotelRoom>(`/hotels/${hotelId}/rooms/${roomId}`);
};
